import { readTotalsMoneyV2, type ProjectionErrorCode, type ProjectionRead, type TotalsMoneyV2 } from "@voltie/localization-web";
import type { BillingTotals } from "@/lib/api";

// GET /api/unit/<id>/billing/totals with money contract 2 (route family F3): the cached
// totals answer (cache state, counts and energy, every scalar null until a pass publishes)
// plus "money_contract": 2 and a money_v2 property following the totals_money_v2 schema
// definition, projected from the same cached answer: billed and estimate groups per
// currency. Money is read from money_v2 only. The legacy money keys (finalized_billed,
// finalized_revenue, active_estimated, ...) are never read, not even for USD, so a broken
// or missing block cannot turn into a dollar amount. A 200 from a server that predates F3
// has no marker and is invalid for the same reason.

/** The legacy money keys of the cached answer: present only when the legacy answer would carry them. */
const LEGACY_MONEY_KEYS = [
  "finalized_billed",
  "finalized_sources",
  "finalized_revenue",
  "revenue_currency",
  "total_revenue",
  "active_estimated",
  "active_estimated_revenue",
  "active_estimate_currency",
] as const;

type LegacyMoneyKey = (typeof LEGACY_MONEY_KEYS)[number];

/** The cached answer without its legacy money keys. */
export type UnitTotalsState = Omit<BillingTotals, LegacyMoneyKey>;

export interface UnitBillingTotals {
  readonly totals: UnitTotalsState;
  readonly money: TotalsMoneyV2;
}

export type UnitBillingTotalsErrorCode = ProjectionErrorCode | "unavailable" | "money_contract" | "field";

export type UnitBillingTotalsRead =
  | { readonly status: "value"; readonly value: UnitBillingTotals }
  | { readonly status: "invalid"; readonly error: UnitBillingTotalsErrorCode; readonly path: string; readonly detail: string };

const ACTIVE_ESTIMATE_STATUSES = ["no_active_session", "estimated", "unavailable"] as const;
const FLAGS = ["computing", "stale", "complete"] as const;
const COUNTS = [
  "completed_sessions",
  "unresolved_billing_count",
  "active_sessions",
  "active_estimate_unknown_count",
  "total_sessions",
] as const;

const invalid = (error: UnitBillingTotalsErrorCode, path: string, detail: string): UnitBillingTotalsRead => ({ status: "invalid", error, path, detail });
const show = (value: unknown) => (typeof value === "string" ? JSON.stringify(value) : String(value));

const COLD_COMPLETENESS = { complete: false, unknown_currency_rows: 0, conflict_rows: 0, invalid_rows: 0, unresolved_billing_rows: 0 };

/** The exact cold-cache placeholder block, or invalid. */
function coldMoney(block: unknown): ProjectionRead<TotalsMoneyV2> {
  if (block === null || block === undefined) return { status: "unavailable" };
  const record = block as Record<string, unknown>;
  const completeness = record.completeness as Record<string, unknown> | undefined;
  const exact =
    typeof block === "object" &&
    !Array.isArray(block) &&
    Object.keys(record).length === 2 &&
    Array.isArray(record.groups) &&
    record.groups.length === 0 &&
    typeof completeness === "object" &&
    completeness !== null &&
    Object.keys(completeness).length === Object.keys(COLD_COMPLETENESS).length &&
    Object.entries(COLD_COMPLETENESS).every(([key, expected]) => completeness[key] === expected);
  if (!exact) {
    return { status: "invalid", error: "completeness", path: "", detail: JSON.stringify(block) };
  }
  return { status: "value", value: Object.freeze({ groups: [], completeness: Object.freeze({ ...COLD_COMPLETENESS }) }) };
}

export function readUnitBillingTotals(body: unknown): UnitBillingTotalsRead {
  if (typeof body !== "object" || body === null || Array.isArray(body)) return invalid("not_object", "", show(body));
  const record = body as Record<string, unknown>;
  if (record.money_contract !== 2) return invalid("money_contract", "money_contract", show(record.money_contract));

  for (const key of FLAGS) {
    if (typeof record[key] !== "boolean") return invalid("field", key, show(record[key]));
  }
  const updated = record.cache_updated_at;
  if (updated !== null && typeof updated !== "string") return invalid("field", "cache_updated_at", show(updated));
  for (const key of COUNTS) {
    const raw = record[key];
    if (raw !== null && (typeof raw !== "number" || !Number.isSafeInteger(raw) || raw < 0)) return invalid("field", key, show(raw));
  }
  const status = record.active_estimate_status;
  if (status !== null && !ACTIVE_ESTIMATE_STATUSES.includes(status as (typeof ACTIVE_ESTIMATE_STATUSES)[number])) {
    return invalid("field", "active_estimate_status", show(status));
  }
  const energy = record.total_energy_kwh;
  if (energy !== null && (typeof energy !== "number" || !Number.isFinite(energy) || energy < 0)) {
    return invalid("field", "total_energy_kwh", show(energy));
  }

  // Before the first pass publishes the server answers no groups and not complete (transport
  // F5); the cache state above already says the totals are unknown, so nothing is read.
  const money = updated === null ? coldMoney(record.money_v2) : readTotalsMoneyV2(record.money_v2);
  if (money.status === "unavailable") return invalid("unavailable", "money_v2", show(record.money_v2));
  if (money.status === "invalid") return invalid(money.error, money.path ? `money_v2.${money.path}` : "money_v2", money.detail);

  const totals = { ...record } as Record<string, unknown>;
  for (const key of [...LEGACY_MONEY_KEYS, "money_contract", "money_v2"]) delete totals[key];
  return {
    status: "value",
    value: Object.freeze({ totals: Object.freeze(totals) as unknown as UnitTotalsState, money: money.value }),
  };
}
